import {
  CoinPrecisionConfig,
  DisplayCurrency,
  PrecisionType,
} from "@shared/types";
import { useCallback } from "react";
import { isNumber } from "lodash";
import { sharedLib } from "@shared/services/shared.service";
import {
  selectDisplayCurrency,
  selectPrecisionsData,
} from "@shared/slices/common.slice";
import { useSelector } from "react-redux";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";

export const usePrecisions = (args?: {
  symbol?: string;
  exchange?: string;
  precisionType: PrecisionType;
}) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const data = useSelector(selectPrecisionsData);
  const { isLoading } =
    sharedLib.sharedApi.common.endpoints.getPrecisions.useQueryState(undefined);

  const getPrecision = useCallback(
    (precisionType: PrecisionType, symbol?: string, exchange?: string) => {
      const BASE_PRECISION_TYPES = [
        "btc_amount",
        "default",
        "general_percent",
        "usd_amount",
        "usd_price",
        "btc_price",
      ];
      let precision: number | undefined;
      if (symbol && exchange) {
        const symbolPrecisionConfig = data?.[exchange]?.[symbol];
        if (symbolPrecisionConfig && precisionType in symbolPrecisionConfig) {
          precision =
            symbolPrecisionConfig[precisionType as keyof CoinPrecisionConfig];
        }
      }
      if (
        !isNumber(precision) &&
        data &&
        BASE_PRECISION_TYPES.includes(precisionType)
      ) {
        precision = data[precisionType] as unknown as number;
      }
      return isNumber(precision) ? precision : data?.default || 0;
    },
    [data]
  );

  const getDisplayCurrencyAmountPrecision = (
    argsDisplayCurrency?: DisplayCurrency
  ) => {
    const _displayCurrency = argsDisplayCurrency ?? displayCurrency;
    return getPrecision(
      _displayCurrency === DISPLAY_CURRENCIES.BTC
        ? PrecisionType.BTC_AMOUNT
        : PrecisionType.USD_AMOUNT
    );
  };

  const getMinimumPrecision = (
    precisionType: PrecisionType,
    symbol: string,
    exchanges: string[]
  ) => {
    let minPrecision: number = 0;
    for (let index = 0; index < exchanges.length; index++) {
      const precision = getPrecision(precisionType, symbol, exchanges[index]);
      if (index === 0 || precision < minPrecision) minPrecision = precision;
    }
    return minPrecision;
  };

  return {
    data,
    precision: args
      ? getPrecision(args.precisionType, args.symbol, args.exchange)
      : 0,
    getPrecision,
    getMinimumPrecision,
    isLoading,
    getDisplayCurrencyAmountPrecision,
  };
};
